import type { ForumPost, ReactionKind, SubmissionReaction } from '@/types/domain';

/**
 * Reglas de la capa social que no dependen de la base de datos.
 *
 * Viven aqui, y no dentro de un componente, porque las usan la interfaz y los
 * tests: el recuento de reacciones y los limites del comentario tienen que
 * decir lo mismo en los dos sitios. Los limites son los MISMOS que impone el
 * servidor (ver la migracion 20260101001500): aqui solo sirven para avisar
 * antes de enviar, nunca como unica defensa.
 */

export const REACTION_KINDS: ReactionKind[] = ['FUEGO', 'FUERZA', 'APLAUSO', 'ALUCINADO'];

export const COMMENT_MAX_LENGTH = 500;
export const COMMENT_MIN_LENGTH = 1;

/** Reacciones vacias: todas las clases a cero, para no tener huecos. */
export function emptyReactionCounts(): Record<ReactionKind, number> {
  return { FUEGO: 0, FUERZA: 0, APLAUSO: 0, ALUCINADO: 0 };
}

/**
 * Cuenta las reacciones de un envio y dice cual puso el propio usuario.
 * Una persona solo tiene una reaccion por envio: cambiarla sustituye.
 */
export function summarizeReactions(
  reactions: Pick<SubmissionReaction, 'user_id' | 'kind'>[],
  currentUserId?: string | null,
): { counts: Record<ReactionKind, number>; mine: ReactionKind | null; total: number } {
  const counts = emptyReactionCounts();
  let mine: ReactionKind | null = null;

  for (const reaction of reactions) {
    if (counts[reaction.kind] === undefined) continue;
    counts[reaction.kind] += 1;
    if (currentUserId && reaction.user_id === currentUserId) mine = reaction.kind;
  }

  return { counts, mine, total: reactions.length };
}

/** `null` si el comentario vale; si no, la clave de error que se traduce. */
export function validateComment(body: string): string | null {
  const text = body.trim();
  if (text.length < COMMENT_MIN_LENGTH) return 'required';
  if (text.length > COMMENT_MAX_LENGTH) return 'commentTooLong';
  return null;
}

// ---------------------------------------------------------------------------
// Foro de la comunidad
// ---------------------------------------------------------------------------

/**
 * Un mensaje del foro admite mas texto que un comentario: aqui se cuenta algo,
 * no se aplaude una marca. El limite es el MISMO que impone la base
 * (migracion 20260101002000); esto solo avisa antes de enviar.
 */
export const FORUM_MAX_LENGTH = 1000;

/** `null` si el mensaje vale; si no, la clave de error que se traduce. */
export function validateForumPost(body: string): string | null {
  const text = body.trim();
  if (text.length < 1) return 'required';
  if (text.length > FORUM_MAX_LENGTH) return 'forumPostTooLong';
  return null;
}

export interface ForumThread {
  post: ForumPost;
  replies: ForumPost[];
}

/**
 * Rehace el tablon a partir de la lista plana que da el repositorio.
 *
 * El tablon llega de lo mas nuevo a lo mas viejo, que es como se lee una
 * portada; las respuestas de cada hilo se devuelven al reves, de la primera a
 * la ultima, porque una conversacion del final al principio no se entiende.
 *
 * Una respuesta cuyo mensaje no esta en la lista —porque moderacion lo ha
 * ocultado— no aparece por su cuenta: el hilo entero desaparece con el.
 */
export function groupForumPosts(posts: ForumPost[]): ForumThread[] {
  const byParent = new Map<string, ForumPost[]>();

  for (const post of posts) {
    if (!post.parent_id) continue;
    const siblings = byParent.get(post.parent_id) ?? [];
    siblings.push(post);
    byParent.set(post.parent_id, siblings);
  }

  return posts
    .filter((post) => !post.parent_id)
    .map((post) => ({
      post,
      replies: (byParent.get(post.id) ?? []).sort((a, b) =>
        a.created_at.localeCompare(b.created_at),
      ),
    }));
}
